import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import Button from 'components/library/Button/Button';
import ErrorAnimation from 'components/library/ErrorAnimation/ErrorAnimation';
import InvestmentSumup from 'components/InvestmentSumup';
import { submitInvestorInformation } from 'store/features/investors/actions';
import { createNewSociety } from 'store/features/society/actions';

import ConfirmationRedirection from './ConfirmationRedirection/ConfirmationRedirection';
import ConfirmationForm from './ConfirmationForm';

import styles from './Confirmation.module.css';
import { AppDispatch } from 'store/types';
import * as statuses from 'store/states';

/**
 *
 * Confirmation page
 * User fill his information and  after submit is redirected to sign the contract on third party website
 *
 */

interface Props {
  status: typeof statuses[keyof typeof statuses],
  wantInvestBySociety: boolean,
  successSociety: boolean,
  successInvestor: boolean,
  actions: {
    getInvestmentsData: (id: number) => void;
    getInvestorInformation: () => void;
  },
  investorInformation: string,
}

export default function Confirmation({
  status, wantInvestBySociety, successSociety, successInvestor, actions, investorInformation,
}: Props) {
  const [hasSubmit, setHasSubmit] = useState(false);
  const [step, setStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [forceRedirection, setForceRedirection] = useState(false);
  const [isRedirectionOpen, setIsRedirectionOpen] = useState(false);
  const [checkValidity, setCheckValidity] = useState(false);

  const { t } = useTranslation();
  const { id } = useParams();

  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    if (status === 'IDLE') {
      actions.getInvestmentsData(parseInt(id || '0'));
    }
  }, [status, actions, id, investorInformation]);

  useEffect(() => {
    if (!isComplete) {
      setCheckValidity(false);
    }
  }, [isComplete]);

  useEffect(() => {
    setIsRedirectionOpen(forceRedirection);
  }, [successSociety, successInvestor, forceRedirection]);


  const handleSubmit = () => {
    if (checkValidity && !hasSubmit) {
      setHasSubmit(true);
      window.setTimeout(() => setHasSubmit(false), 10 * 1000);
      return (!wantInvestBySociety) ? dispatch(submitInvestorInformation(() => setForceRedirection(true))) : dispatch(createNewSociety(() => setForceRedirection(true)));
    } else {
      setCheckValidity(true);
    }
  }

  if (isRedirectionOpen) {
    return (
      <ConfirmationRedirection
        back={() => {
          setForceRedirection(false);
          setIsRedirectionOpen(false);
          setCheckValidity(false);
          setHasSubmit(false);
        }}
      />
    );
  }

  return (
    <>
      {
        // Cancel modal when user cancel the operation on third party website
        (new URLSearchParams(window.location.search).get('status') === 'cancel') && (
          <div className={styles.background}>
            <section className={styles.modal}>
              <ErrorAnimation />
              <h1 data-cy="confirmation-loi-title">
                {t('coordonnate.loi.title')}
              </h1>
              <p>
                {t('coordonnate.loi.description')}
              </p>
              <Button
                label={t('coordonnate.loi.try')}
                onButtonClick={() => setForceRedirection(true)}
                dataCy="confirmation-loi-retry"
              />
            </section>
          </div>
        )
      }
      <section className={styles.content}>
        <div className={styles.row}>
          <div className={styles.container}>
            <ConfirmationForm
              wantInvestBySociety={wantInvestBySociety}
              setIsComplete={setIsComplete}
              step={step}
              setStep={setStep}
              submit={handleSubmit}
              checkValidity={checkValidity}
            >
              <Button
                label={t('coordonnate.signLetter')}
                className={`${styles.submit} ${styles.displayMobile}`}
                disabled={!isComplete || hasSubmit}
                onButtonClick={handleSubmit}
                dataCy="mobile-confirmation-submit"
              />
            </ConfirmationForm>
          </div>
          <InvestmentSumup disabled page={(wantInvestBySociety && step === 0) ? 'confirmation-society' : 'confirmation'} >
            {
              (isComplete)
              && (
                <Button
                  icon={hasSubmit && <FontAwesomeIcon icon={faSpinner} spin />}
                  label={t('coordonnate.signLetter')}
                  className={`${styles.submit} ${styles.displayDesktop}`}
                  disabled={!isComplete || hasSubmit}
                  onButtonClick={handleSubmit}
                  dataCy="desktop-confirmation-submit"
                />
              )
            }
          </InvestmentSumup>
        </div>
      </section>
    </>
  );
}
